/**
 * LES ONZE REGIONS DE L'ILE 2.
 *
 * Elles viennent d'une carte de collection que le proprietaire a photographiee:
 * une ile decoupee en « hangouts » nommes — canyon de cristal, chute d'etoile,
 * paradis polaire, foret magique, bosquet, jardin fleuri, ferme, lac lilas,
 * desert scintillant, plage, et l'anse des nuages.
 *
 * LA DISPOSITION EST UNE COLONNE, PAS UNE REGLE. Le proprietaire a choisi « un
 * anneau, un coeur sauvage »: les regions douces font le tour par les cotes,
 * les spectaculaires sont au centre, et l'ile se decouvre en marchant vers
 * l'interieur depuis n'importe quel rivage. Cela ne s'ecrit nulle part dans le
 * code: c'est `rayon` — 0 au coeur, 1 sur le rivage — et rien d'autre. Un test
 * le verifie comme une PROPRIETE (toute region a rayon >= 0,7 touche l'eau,
 * aucune region a rayon <= 0,4 ne la touche), ce qui vaut mieux qu'un
 * commentaire qu'on croit sur parole.
 *
 * L'ANGLE EST EN TOURS DEPUIS L'EST, pour qu'on puisse lire la table comme une
 * boussole: 0 = est, 0,25 = sud, 0,5 = ouest, 0,75 = nord. On arrive du monde 1
 * par l'OUEST, donc la Plage de la Brise est a 0,5 — c'est la premiere chose
 * qu'on voit en descendant d'avion, et c'est voulu.
 */
import { ILE2_COTE, ILE2_X0, ILE2_Z0 } from '../constants.ts'
import type { RegionDef, RegionId } from '../types/ile2.ts'
import type { Vec2 } from '../types/world.ts'

/**
 * L'ORDRE, c'est-a-dire le contrat d'index.
 *
 * La region dominante de chaque metre carre est rangee dans un `Uint8Array`:
 * c'est cette liste qui dit ce que vaut l'octet. Meme role que `ORDRE` pour les
 * cinq biomes de l'ile 1 — on n'y touche pas sans y penser.
 */
export const ORDRE_REGIONS: readonly RegionId[] = [
  'anse_nuages',
  'plage_brise',
  'sables_scintillants',
  'lac_lilas',
  'ferme_amitie',
  'jardin_scintillant',
  'bosquet_rires',
  'foret_fabula',
  'canyon_cristal',
  'cascade_etoile',
  'paradis_polaire',
]

export const NB_REGIONS = ORDRE_REGIONS.length

export const REGIONS: Record<RegionId, RegionDef> = {
  // ------------------------------------------------------------- l'anneau

  /**
   * L'ANSE DES NUAGES — la region barree sur la carte du proprietaire, et
   * gardee quand meme: une crique basse et brumeuse, du sable pale, des vasques
   * d'eau claire. C'est le contrepoint des pics enneiges, tout en bas.
   */
  anse_nuages: {
    id: 'anse_nuages',
    nom: 'Anse des Nuages',
    biome: 'plage',
    sol: 'sable',
    angle: 0.63,
    rayon: 0.84,
    force: 0.9,
    relief: {
      socle: 4.4,
      houle: { ampli: 2.8, onde: 55 },
      replat: 0.55,
      crete: { ampli: 1.8, onde: 70, tourne: 0.9 },
      marche: 0,
      grain: 0.3,
    },
    cote: { greve: 1.2, largeur: 58, chute: 16 },
    couleurSol: 0xdfe3e8,
    couleurSolVariante: 0xf4f1e6,
    couleurBrume: 0xe8eef6,
    vitesse: 0.88,
    props: { coquillage: 150, herbe: 260, caillou: 40, fleur: 40 },
    densiteNoeuds: 0.12,
    melange: [
      { kind: 'palmier', poids: 0.55 },
      { kind: 'buisson_baies', poids: 0.45 },
    ],
  },

  /**
   * LA PLAGE DE LA BRISE — la porte de l'ile. C'est ici qu'atterrit l'avion du
   * monde 1, ici qu'on echoue en nageant, et c'est donc la region la plus PLATE
   * de la table: une piste a sa propre tolerance de denivele, et un batiment
   * qu'on ne peut pas poser est un batiment qui n'existe pas.
   */
  plage_brise: {
    id: 'plage_brise',
    nom: 'Plage de la Brise',
    biome: 'plage',
    sol: 'sable',
    angle: 0.5,
    rayon: 0.86,
    force: 0.95,
    relief: {
      socle: 2.6,
      houle: { ampli: 1.5, onde: 40 },
      replat: 0.72,
      crete: { ampli: 0.8, onde: 90, tourne: 0.3 },
      marche: 0,
      grain: 0.25,
    },
    cote: { greve: 1.0, largeur: 66, chute: 16 },
    couleurSol: 0xe8cf86,
    couleurSolVariante: 0xf8eec6,
    couleurBrume: 0xffe6bd,
    vitesse: 0.85,
    props: { coquillage: 220, herbe: 200, caillou: 32, fleur: 20 },
    densiteNoeuds: 0.1,
    melange: [{ kind: 'palmier', poids: 1 }],
  },

  /**
   * LES SABLES SCINTILLANTS — le desert, dos au monde 1. Une oasis creusee au
   * milieu: elle est SOUS le niveau de la mer, comme tout ce qui est de l'eau
   * dans ce moteur, et la nappe unique la remplit sans qu'on ait rien a ecrire.
   */
  sables_scintillants: {
    id: 'sables_scintillants',
    nom: 'Sables Scintillants',
    biome: 'desert',
    sol: 'sable',
    angle: 0.06,
    rayon: 0.84,
    force: 1.05,
    relief: {
      socle: 7.5,
      houle: { ampli: 3.6, onde: 90 },
      replat: 0.6,
      crete: { ampli: 2.0, onde: 110, tourne: 1.9 },
      marche: 0,
      dune: { ampli: 1.5, onde: 34, cap: 0.62 },
      grain: 0.3,
    },
    cote: { greve: 1.6, largeur: 62, chute: 15 },
    couleurSol: 0xe2a95a,
    couleurSolVariante: 0xf0cf8a,
    couleurBrume: 0xffd69f,
    vitesse: 0.85,
    props: { caillou: 56, herbe: 90, fleur: 22, coquillage: 60, cactus: 30 },
    densiteNoeuds: 0.13,
    melange: [{ kind: 'cactus', poids: 1 }],
    lac: { rayon: 15, fond: -2 },
  },

  /**
   * LE LAC LILAS — une cuvette et un miroir d'eau, face a la camera. Sa couleur
   * ne vient pas de l'eau (il n'y a qu'une nappe, et elle est bleue): elle
   * vient de son FOND, qu'on voit au travers. C'est la meme facon de faire que
   * les hauts-fonds turquoise du littoral.
   */
  lac_lilas: {
    id: 'lac_lilas',
    nom: 'Lac Lilas',
    biome: 'foret',
    sol: 'herbe',
    angle: 0.2,
    rayon: 0.74,
    force: 1.0,
    relief: {
      socle: 4.2,
      houle: { ampli: 3.0, onde: 65 },
      replat: 0.7,
      crete: { ampli: 1.5, onde: 75, tourne: 2.4 },
      marche: 0,
      grain: 0.35,
    },
    cote: { greve: 1.8, largeur: 54, chute: 13 },
    couleurSol: 0x9d7fc4,
    couleurSolVariante: 0xcfa8e2,
    couleurBrume: 0xe3d4f5,
    vitesse: 1,
    props: { herbe: 900, fleur: 420, champignon: 40, caillou: 24 },
    densiteNoeuds: 0.4,
    melange: [
      { kind: 'arbre_lilas', poids: 0.45 },
      { kind: 'arbre', poids: 0.2 },
      { kind: 'buisson_baies', poids: 0.35 },
    ],
    lac: { rayon: 44, fond: -3 },
  },

  /**
   * LA FERME DE L'AMITIE — des collines douces et surtout des REPLATS: c'est la
   * region ou l'on batit, et son `replat` est le plus fort de la table. Ses
   * cultures ne sont pas des noeuds recoltables mais des BATIMENTS (`potager`,
   * `serre`), qui ont deja leur table `recolte`: une ferme se cultive, elle ne
   * se ramasse pas.
   */
  ferme_amitie: {
    id: 'ferme_amitie',
    nom: "Ferme de l'Amitié",
    biome: 'ferme',
    sol: 'terre',
    angle: 0.9,
    rayon: 0.78,
    force: 0.95,
    relief: {
      socle: 5.5,
      houle: { ampli: 2.4, onde: 70 },
      replat: 0.9,
      crete: { ampli: 1.0, onde: 90, tourne: 0.15 },
      marche: 0,
      grain: 0.22,
    },
    cote: { greve: 2.0, largeur: 52, chute: 12 },
    couleurSol: 0x8fae4c,
    couleurSolVariante: 0xc7b56a,
    couleurBrume: 0xe6efc0,
    vitesse: 1,
    props: { herbe: 1200, fleur: 260, caillou: 18 },
    densiteNoeuds: 0.12,
    melange: [
      { kind: 'arbre', poids: 0.6 },
      { kind: 'buisson_baies', poids: 0.4 },
    ],
  },

  /**
   * LE JARDIN SCINTILLANT — des terrasses plates, des allees, et beaucoup de
   * fleurs. Il n'a besoin d'AUCUN biome a lui: tout son caractere tient dans sa
   * couleur de sol et dans une densite de `fleur` que rien d'autre n'approche.
   * C'est exactement l'argument pour lequel les regions ne sont pas des biomes.
   */
  jardin_scintillant: {
    id: 'jardin_scintillant',
    nom: 'Jardin Scintillant',
    biome: 'foret',
    sol: 'herbe',
    angle: 0.76,
    rayon: 0.8,
    force: 0.9,
    relief: {
      socle: 6.8,
      houle: { ampli: 3.2, onde: 60 },
      replat: 0.85,
      crete: { ampli: 1.4, onde: 80, tourne: 1.1 },
      marche: 0,
      grain: 0.25,
    },
    cote: { greve: 2.0, largeur: 52, chute: 12 },
    couleurSol: 0x63b05a,
    couleurSolVariante: 0xa8d46e,
    couleurBrume: 0xffe0f0,
    vitesse: 1,
    props: { herbe: 1100, fleur: 1250, champignon: 24, caillou: 14 },
    densiteNoeuds: 0.3,
    melange: [
      { kind: 'buisson_baies', poids: 0.7 },
      { kind: 'arbre', poids: 0.3 },
    ],
  },

  // ------------------------------------------------------- la bande mediane

  /** LE BOSQUET DES RIRES — la jungle: vallonnee, dense, bruyante. */
  bosquet_rires: {
    id: 'bosquet_rires',
    nom: 'Bosquet des Rires',
    biome: 'jungle',
    sol: 'terre',
    angle: 0.25,
    rayon: 0.55,
    force: 1.1,
    relief: {
      socle: 10.5,
      houle: { ampli: 6.5, onde: 60 },
      replat: 0.45,
      crete: { ampli: 6.5, onde: 55, tourne: 2.7 },
      marche: 0,
      grain: 0.45,
    },
    cote: { greve: 2.6, largeur: 48, chute: 11 },
    couleurSol: 0x3f8f39,
    couleurSolVariante: 0x8fc23c,
    couleurBrume: 0xc8ecb0,
    vitesse: 0.95,
    props: { herbe: 1500, fleur: 300, champignon: 90, caillou: 26, palmier: 40 },
    densiteNoeuds: 0.66,
    melange: [
      { kind: 'arbre', poids: 0.5 },
      { kind: 'palmier', poids: 0.28 },
      { kind: 'buisson_baies', poids: 0.22 },
    ],
  },

  /** LA FORET FABULA — la futaie magique, ses clairieres et ses arbres pourpres. */
  foret_fabula: {
    id: 'foret_fabula',
    nom: 'Forêt Fabula',
    biome: 'foret',
    sol: 'herbe',
    angle: 0.6,
    rayon: 0.52,
    force: 1.15,
    relief: {
      socle: 12.5,
      houle: { ampli: 8.5, onde: 90 },
      replat: 0.5,
      crete: { ampli: 7.5, onde: 70, tourne: 0.65 },
      marche: 0,
      grain: 0.4,
    },
    cote: { greve: 2.4, largeur: 50, chute: 10 },
    couleurSol: 0x4a7a54,
    couleurSolVariante: 0x8c6bb0,
    couleurBrume: 0xd6c6ee,
    vitesse: 1,
    props: { herbe: 1400, fleur: 380, champignon: 120, caillou: 30 },
    densiteNoeuds: 0.62,
    melange: [
      { kind: 'arbre', poids: 0.42 },
      { kind: 'arbre_lilas', poids: 0.24 },
      { kind: 'buisson_baies', poids: 0.34 },
    ],
  },

  // ------------------------------------------------------- le coeur sauvage

  /**
   * LE CANYON DE CRISTAL — une GORGE, c'est-a-dire une crete d'amplitude
   * NEGATIVE: on ne l'a pas ecrite a la main, on a mis un moins devant un
   * nombre. C'est la seule region qui serait indiscernable de la montagne sans
   * un noeud a elle, et c'est pour ca que `cristal` existe.
   */
  canyon_cristal: {
    id: 'canyon_cristal',
    nom: 'Canyon de Cristal',
    biome: 'cristal',
    sol: 'cristal',
    angle: 0.05,
    rayon: 0.3,
    force: 1.3,
    relief: {
      socle: 17.5,
      houle: { ampli: 5.5, onde: 100 },
      replat: 0.4,
      crete: { ampli: -22, onde: 45, tourne: 1.35 },
      marche: 3.0,
      grain: 0.5,
    },
    cote: { greve: 4.2, largeur: 44, chute: 8 },
    couleurSol: 0x8f9bd6,
    couleurSolVariante: 0xd7c4ee,
    couleurBrume: 0xd8dcff,
    vitesse: 0.92,
    props: { caillou: 130, herbe: 180, fleur: 30 },
    densiteNoeuds: 0.34,
    melange: [
      { kind: 'cristal', poids: 0.6 },
      { kind: 'rocher', poids: 0.4 },
    ],
  },

  /**
   * LA CASCADE DE L'ETOILE — une falaise en gradins et le torrent qui la
   * descend jusqu'au Lac Lilas.
   *
   * Elle n'a PAS de lac de tete, et c'est une mesure qui l'a decide: l'eau du
   * jeu est ce qui est sous le niveau de la mer, donc une vasque a vingt et un
   * metres d'altitude est un puits de vingt-trois metres creuse dans un
   * plateau. Elle donnait soixante-dix-huit points de rive qu'on ne pouvait
   * plus remonter a la nage. Le torrent, lui, descend vers un lac qui est
   * DEJA en bas, ce qui est la meme image sans le trou.
   *
   * Le secteur vise reste dans la table pour le jour ou la lame d'eau tombante
   * de l'ile 1 saura se mettre au pluriel.
   */
  cascade_etoile: {
    id: 'cascade_etoile',
    nom: "Cascade de l'Étoile",
    biome: 'montagne',
    sol: 'roche',
    angle: 0.3,
    rayon: 0.2,
    force: 1.15,
    relief: {
      socle: 21,
      houle: { ampli: 7.5, onde: 80 },
      replat: 0.5,
      crete: { ampli: 13, onde: 60, tourne: 2.15 },
      marche: 2.5,
      grain: 0.45,
    },
    cote: { greve: 3.6, largeur: 46, chute: 9 },
    couleurSol: 0x8f96ab,
    couleurSolVariante: 0x6f9a62,
    couleurBrume: 0xd8e4ee,
    vitesse: 0.95,
    props: { herbe: 420, caillou: 110, fleur: 70 },
    densiteNoeuds: 0.3,
    melange: [
      { kind: 'rocher', poids: 0.55 },
      { kind: 'arbre', poids: 0.45 },
    ],
    chute: { secteur: Math.PI * 1.25, demiSecteur: Math.PI * 0.2 },
  },

  /**
   * LE PARADIS POLAIRE — le toit de l'ile. Des pics, des gradins, et la seule
   * region ou la peche rend du poisson rare (c'est le biome `glacier` qui le
   * dit, et il le disait deja).
   */
  paradis_polaire: {
    id: 'paradis_polaire',
    nom: 'Paradis Polaire',
    biome: 'glacier',
    sol: 'neige',
    angle: 0.75,
    rayon: 0.34,
    force: 1.3,
    relief: {
      socle: 25,
      houle: { ampli: 7.5, onde: 90 },
      replat: 0.3,
      crete: { ampli: 26, onde: 55, tourne: 0.42 },
      marche: 3.0,
      grain: 0.5,
    },
    cote: { greve: 9.5, largeur: 26, chute: 4.5 },
    couleurSol: 0xeaf5ff,
    couleurSolVariante: 0x8ad9ea,
    couleurBrume: 0xdcefff,
    vitesse: 0.8,
    props: { glacon: 120, caillou: 28 },
    densiteNoeuds: 0.2,
    melange: [
      { kind: 'bloc_glace', poids: 0.65 },
      { kind: 'rocher', poids: 0.35 },
    ],
  },
}

/**
 * OU L'ON ARRIVE DU MONDE 1: sur la cote OUEST, face a chez soi.
 *
 * L'aerodrome etait au centre geometrique du carre de 200 m. Le centre d'un
 * carre de 700 m est a 350 m du rivage, au milieu du coeur sauvage — c'est-a-
 * dire exactement la ou l'on ne veut pas d'une piste. On le pose donc sur la
 * Plage de la Brise, ce que les constantes annoncaient deja: « il devient un
 * aeroport cotier, c'est-a-dire exactement la ou on veut arriver quand on vient
 * du monde 1 ».
 *
 * `x = ILE2_X0 + 105`, soit CINQ METRES A L'EST de l'ancien aerodrome (1300).
 * `ILE2_X0` est fige, donc c'est le seul bouton qui reste, et il ne tourne que
 * dans le bon sens: la traversee ne peut que s'allonger, jamais retrecir sous
 * les trente secondes.
 */
export const SITE_AERODROME: Vec2 = { x: ILE2_X0 + 105, z: ILE2_Z0 + ILE2_COTE / 2 }

/**
 * Rayon du disque degage autour de l'aerodrome, en metres.
 *
 * Il doit couvrir la demi-profondeur de l'aerogare, le tablier et les 22 m de
 * piste: sans lui, un seul palmier en travers suffirait a ce que le seul
 * aeroport du deuxieme monde ne se pose jamais. Le semis de l'ile ET la
 * recherche d'emplacement du serveur lisent CETTE constante — c'est la meme
 * question, il n'y a donc qu'une reponse.
 */
export const RAYON_AERODROME = 40

/** Rayon du replat franc autour de l'aerodrome, et distance ou il se fond. */
export const RAYON_PLAT_AERO = 34
export const RAYON_FONDU_AERO = 70

/** La region, par son identifiant. */
export function regionDef(id: RegionId): RegionDef {
  return REGIONS[id]
}

/**
 * Le point de la region, en coordonnees du monde.
 *
 * Le rayon 1 tombe sur le bord du CARRE dans toutes les directions, pas sur un
 * cercle inscrit: l'ile est carree, la table doit le savoir, sinon les coins
 * n'appartiendraient a personne.
 */
export function pointSemence(def: RegionDef): Vec2 {
  const demi = ILE2_COTE / 2
  const a = def.angle * Math.PI * 2
  const cx = Math.cos(a)
  const cz = Math.sin(a)
  const port = demi / Math.max(Math.abs(cx), Math.abs(cz))
  return {
    x: ILE2_X0 + demi + cx * def.rayon * port,
    z: ILE2_Z0 + demi + cz * def.rayon * port,
  }
}
